import { getPersonIdentityProfileSuggestions } from './aiReviewPolicy';
import { getCareSourceKnowledgeSuggestions } from './careSourceStatusModel';
import {
  buildReviewableCandidatesFromCareSource,
  getStructuredCandidateDestination,
} from './careSourceReviewableCandidates';
import { getDirectProfileReviewItems, getProfileChapterLabel } from './DirectProfileOrganizationReview';
import { getCareKnowledgePromotionDecision } from './careKnowledgeDurabilityPolicy';

export const DESTINATION_ORDER = Object.freeze([
  'Person',
  'Communication',
  'What Helps',
  'Watch For',
  'Daily Life',
  'Safety',
  'Health & Care',
  'Goals',
  'Timeline',
  'Document',
  'Medications',
  'Allergies',
  'Health Profile',
  'Contacts',
  'Profile',
]);

export const sortDestinationEntries = (counts = {}) => (
  Object.entries(counts)
    .filter(([, count]) => Number(count) > 0)
    .sort(([left], [right]) => {
      const leftIndex = DESTINATION_ORDER.indexOf(left);
      const rightIndex = DESTINATION_ORDER.indexOf(right);
      if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
      if (leftIndex === -1) return 1;
      if (rightIndex === -1) return -1;
      return leftIndex - rightIndex;
    })
);

export const formatDestinationCount = (label, count) => {
  const singular = {
    Goals: 'Goal',
    Medications: 'Medication',
    Allergies: 'Allergy',
    Timeline: 'Timeline item',
    Document: 'Document section',
    'Health Profile': 'Diagnosis',
    Contacts: 'Contact',
  }[label] || label;
  return `${count} ${count === 1 ? singular : label}`;
};

const addDestination = (destinations, label, count = 1) => {
  destinations[label] = (destinations[label] || 0) + count;
};

const getReviewableKnowledgeSuggestions = (source = {}) => (
  getCareSourceKnowledgeSuggestions(source).filter((item) => (
    getCareKnowledgePromotionDecision(item).origin !== 'caregiver_typed'
  ))
);

export const buildManualReviewSourceSummary = (source = {}, options = {}) => {
  if (source.reviewState === 'reviewed') {
    return { pendingTotal: 0, destinations: {} };
  }

  const decisions = source.directProfileReviewDecisions || {};
  const destinations = {};

  getDirectProfileReviewItems(source, getReviewableKnowledgeSuggestions(source))
    .filter((item) => !decisions[item.clientId])
    .forEach((item) => addDestination(
      destinations,
      getProfileChapterLabel(item.profileChapterId)
    ));

  if (options.structuredDestinations) {
    Object.entries(options.structuredDestinations).forEach(([label, count]) => {
      addDestination(destinations, label, count);
    });
  } else {
    buildReviewableCandidatesFromCareSource(source, { includeMedication: true }).forEach((candidate) => {
      addDestination(destinations, getStructuredCandidateDestination(candidate.domain));
    });
  }

  getPersonIdentityProfileSuggestions(source).forEach((group) => {
    const pendingCount = group.items.filter(
      (_, index) => !decisions[`legacy-profile:${group.key}:${index}`]
    ).length;
    if (pendingCount) addDestination(destinations, 'Person', pendingCount);
  });

  return {
    pendingTotal: Object.values(destinations).reduce((total, count) => total + count, 0),
    destinations,
  };
};

export const buildManualReviewQueueSummary = (sources = [], structuredDestinationsBySource = {}) => (
  sources.reduce((summary, source) => {
    const sourceSummary = buildManualReviewSourceSummary(source, {
      structuredDestinations: structuredDestinationsBySource[source.id],
    });
    summary.pendingTotal += sourceSummary.pendingTotal;
    Object.entries(sourceSummary.destinations).forEach(([label, count]) => {
      summary.destinations[label] = (summary.destinations[label] || 0) + count;
    });
    return summary;
  }, {
    pendingTotal: 0,
    destinations: {},
  })
);

export const getReviewSourceGroupKey = (source = {}) => (
  String(source.captureId || source.sourceCaptureId || source.id || '').trim()
);

export const groupCareSourcesByCapture = (sources = []) => (
  [...sources.reduce((groups, source) => {
    const key = getReviewSourceGroupKey(source);
    if (!key) return groups;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(source);
    return groups;
  }, new Map()).entries()]
    .map(([key, groupedSources]) => ({ key, sources: groupedSources }))
);

export const groupManualReviewSourcesByCapture = groupCareSourcesByCapture;

// Kept as an alias for existing consumers while the queue is manual-review only.
export const buildQueueExtractionSummary = buildManualReviewQueueSummary;
